import axios from 'axios';
import io from 'socket.io-client';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  withCredentials: true,
});

// Attach token if stored (future-proof)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('employer_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear stored data and redirect to login
      console.log('🔐 EMPLOYER API: Token expired or invalid, redirecting to login');
      localStorage.removeItem('employer_token');
      localStorage.removeItem('employer_profile');

      // Disconnect socket if connected
      if (chatAPI.socket) {
        chatAPI.socket.disconnect();
        chatAPI.socket = null;
      }

      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Employee API functions
export const employeeAPI = {
  // Get all employees for the logged-in employer
  getEmployees: (params = {}) => api.get('/employees', { params }),
  
  // Get single employee
  getEmployee: (id) => api.get(`/employees/${id}`),
  
  // Create new employee
  createEmployee: (data) => api.post('/employees', data),
  
  // Update employee
  updateEmployee: (id, data) => api.put(`/employees/${id}`, data),
  
  // Delete employee
  deleteEmployee: (id) => api.delete(`/employees/${id}`),
  
  // Reset employee password
  resetEmployeePassword: (id) => api.put(`/employees/${id}/reset-password`)
};

// Timesheet API for employer side
export const timesheetAPI = {
  // Get timesheets for employer (optionally filter by employeeId or other params)
  getTimesheets: (params = {}) => api.get('/timesheets', { params }),

  // Get single timesheet
  getTimesheet: (id) => api.get(`/timesheets/${id}`),

  // Approve / reject a timesheet with enhanced options
  approveTimesheet: (id, data) => api.put(`/timesheets/${id}/approve`, data),

  // Bulk approve multiple timesheets
  bulkApproveTimesheets: (timesheetIds, data) => api.put('/timesheets/bulk-approve', {
    timesheetIds,
    ...data
  }),

  // Update timesheet (for hour adjustments by employer)
  updateTimesheet: (id, data) => api.put(`/timesheets/${id}`, data)
};

// Employer profile API
export const employerAPI = {
  // Get own profile
  getProfile: () => api.get('/employers/profile'),
  
  // Update own profile
  updateProfile: (data) => api.put('/employers/profile', data),
  
  // Upload company logo
  uploadLogo: (formData) => {
    return api.post('/upload/employer-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
};

export const invoiceAPI = {
  // Fetch invoices for this employer (supports query params for filtering)
  getInvoices: (params = {}) => api.get('/invoices', { params }),
};

// Chat API functions
export const chatAPI = {
  // Socket.IO instance
  socket: null,

  // Initialize socket connection
  initSocket: () => {
    const token = localStorage.getItem('employer_token');

    if (!token) {
      console.error('No token found for socket authentication');
      return null;
    }

    // If socket already exists and is connected, return it
    if (chatAPI.socket && chatAPI.socket.connected) {
      return chatAPI.socket;
    }

    // If socket exists but is not connected, disconnect it first
    if (chatAPI.socket) {
      chatAPI.socket.disconnect();
    }

    try {
      chatAPI.socket = io(process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000', {
        auth: {
          token
        },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: true
      });

      chatAPI.socket.on('connect', () => {
        console.log('Connected to chat server:', chatAPI.socket.id);
        // Set user online when connected
        if (chatAPI.socket && chatAPI.socket.connected) {
          chatAPI.setOnline();
        }
      });

      chatAPI.socket.on('disconnect', () => {
        console.log('Disconnected from chat server');
      });

      chatAPI.socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        // If connection fails due to auth, redirect to login
        if (error.message?.includes('Authentication') || error.message?.includes('unauthorized')) {
          console.log('🔐 EMPLOYER API: Socket auth failed, redirecting to login');
          localStorage.removeItem('employer_token');
          localStorage.removeItem('employer_profile');
          window.location.href = '/login';
        }
      });

      return chatAPI.socket;
    } catch (error) {
      console.error('Failed to create socket connection:', error);
      return null;
    }
  },

  // Disconnect socket
  disconnectSocket: () => {
    if (chatAPI.socket) {
      chatAPI.socket.disconnect();
      chatAPI.socket = null;
    }
  },

  // Check if socket is connected and ready
  isConnected: () => {
    return chatAPI.socket && chatAPI.socket.connected;
  },

  // =================== REST API METHODS ===================

  // Conversations
  getConversations: (params = {}) => api.get('/chat/conversations', { params }),
  createConversation: (data) => {
    console.log('📤 EMPLOYER API: Creating conversation with data:', data);
    return api.post('/chat/conversations', data).then(response => {
      console.log('📥 EMPLOYER API: Conversation created response:', response.data);
      return response;
    }).catch(error => {
      console.error('❌ EMPLOYER API: Conversation creation failed:', {
        error: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    });
  },
  getConversation: (id) => api.get(`/chat/conversations/${id}`),
  deleteConversation: (id) => api.delete(`/chat/conversations/${id}`),

  // Messages
  getMessages: (conversationId, params = {}) => api.get(`/chat/conversations/${conversationId}/messages`, { params }),
  sendMessage: (conversationId, data) => api.post(`/chat/conversations/${conversationId}/messages`, data),
  sendTask: (conversationId, taskData) => {
    console.log('📤 EMPLOYER API: Sending task with data:', taskData);
    return api.post(`/chat/conversations/${conversationId}/messages`, {
      content: `Task assigned: ${taskData.taskTitle}`,
      messageType: 'task',
      metadata: {
        task: {
          taskTitle: taskData.taskTitle,
          taskDescription: taskData.taskDescription,
          dueDate: new Date(taskData.dueDate),
          priority: taskData.priority,
          status: 'pending',
          assignedTo: {
            userId: taskData.assignedToId,
            userType: 'employee'
          }
        }
      }
    });
  },
  editMessage: (messageId, data) => api.put(`/chat/messages/${messageId}`, data),
  deleteMessage: (messageId) => api.delete(`/chat/messages/${messageId}`),
  markMessageAsRead: (messageId) => api.put(`/chat/messages/${messageId}/read`),

  // Notifications
  getNotifications: (params = {}) => api.get('/chat/notifications', { params }),
  markNotificationAsRead: (notificationId) => api.put(`/chat/notifications/${notificationId}/read`),
  markAllNotificationsAsRead: () => api.put('/chat/notifications/read-all'),
  getUnreadNotificationCount: () => api.get('/chat/notifications/unread-count'),

  // File uploads
  uploadFile: (formData) => {
    return api.post('/chat/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  getFileInfo: (publicId) => api.get(`/chat/files/${publicId}`),
  deleteFile: (publicId) => api.delete(`/chat/files/${publicId}`),

  // Typing indicators
  getTypingUsers: (conversationId) => api.get(`/chat/conversations/${conversationId}/typing`),

  // =================== SOCKET.IO METHODS ===================

  // Join conversation room
  joinConversation: (conversationId) => {
    if (chatAPI.socket) {
      console.log('🔗 EMPLOYER API: Joining conversation room:', conversationId);
      chatAPI.socket.emit('join-conversation', { conversationId });
      
      // Listen for join confirmation
      chatAPI.socket.once('joined-conversation', (data) => {
        console.log('✅ EMPLOYER API: Successfully joined conversation:', data);
      });
      
      chatAPI.socket.once('error', (error) => {
        console.error('❌ EMPLOYER API: Error joining conversation:', error);
      });
    } else {
      console.error('❌ EMPLOYER API: No socket available to join conversation');
    }
  },

  // Leave conversation room
  leaveConversation: (conversationId) => {
    if (chatAPI.socket) {
      chatAPI.socket.emit('leave-conversation', { conversationId });
    }
  },

  // Send message via socket
  sendMessageSocket: (conversationId, content, messageType = 'text', metadata = {}, replyTo = null) => {
    if (chatAPI.socket) {
      chatAPI.socket.emit('send-message', {
        conversationId,
        content,
        messageType,
        metadata,
        replyTo
      });
    }
  },

  // Typing indicators
  startTyping: (conversationId) => {
    if (chatAPI.socket) {
      chatAPI.socket.emit('typing-start', { conversationId });
    }
  },

  stopTyping: (conversationId) => {
    if (chatAPI.socket) {
      chatAPI.socket.emit('typing-stop', { conversationId });
    }
  },

  // Mark messages as read
  markMessagesAsRead: async (conversationId, messageIds) => {
    try {
      console.log('📖 EMPLOYER API: Marking messages as read for conversation:', conversationId);
      
      // Call REST API to mark messages as read in database
      const response = await api.put(`/chat/conversations/${conversationId}/messages/read`);
      console.log('📖 EMPLOYER API: Mark as read response:', response.data);
      
      // Also emit socket event for real-time updates
      if (chatAPI.socket) {
        chatAPI.socket.emit('mark-as-read', { conversationId, messageIds });
      }
      
      return response;
    } catch (error) {
      console.error('❌ EMPLOYER API: Error marking messages as read:', error);
      throw error;
    }
  },

  // Update task status
  updateTaskStatus: async (messageId, status) => {
    try {
      console.log('📋 EMPLOYER API: Updating task status:', { messageId, status });
      
      const response = await api.put(`/chat/messages/${messageId}/task-status`, { status });
      console.log('📋 EMPLOYER API: Task status updated response:', response.data);
      
      return response;
    } catch (error) {
      console.error('❌ EMPLOYER API: Error updating task status:', error);
      throw error;
    }
  },

  // Message reactions
  addReaction: (messageId, emoji) => {
    if (chatAPI.socket) {
      chatAPI.socket.emit('add-reaction', { messageId, emoji });
    }
  },

  removeReaction: (messageId, emoji) => {
    if (chatAPI.socket) {
      chatAPI.socket.emit('remove-reaction', { messageId, emoji });
    }
  },

  // User status
  setOnline: () => {
    if (chatAPI.socket) {
      chatAPI.socket.emit('user-online');
    }
  },

  // =================== EVENT LISTENERS ===================

  // Message events
  onMessageReceived: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('message-received', callback);
      return () => chatAPI.socket && chatAPI.socket.off('message-received', callback);
    }
    return () => {}; // Return empty function if socket is null
  },

  onMessageSent: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('message-sent', callback);
      return () => chatAPI.socket && chatAPI.socket.off('message-sent', callback);
    }
    return () => {}; // Return empty function if socket is null
  },

  // Typing events
  onTypingIndicator: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('typing-indicator', callback);
      return () => chatAPI.socket && chatAPI.socket.off('typing-indicator', callback);
    }
    return () => {}; // Return empty function if socket is null
  },

  // Read receipt events
  onMessagesRead: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('messages-read', callback);
      return () => chatAPI.socket && chatAPI.socket.off('messages-read', callback);
    }
    return () => {}; // Return empty function if socket is null
  },

  // Reaction events
  onReactionAdded: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('reaction-added', callback);
      return () => chatAPI.socket && chatAPI.socket.off('reaction-added', callback);
    }
    return () => {}; // Return empty function if socket is null
  },

  onReactionRemoved: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('reaction-removed', callback);
      return () => chatAPI.socket && chatAPI.socket.off('reaction-removed', callback);
    }
    return () => {}; // Return empty function if socket is null
  },

  // User status events
  onUserStatusChanged: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('user-status-changed', callback);
      return () => chatAPI.socket && chatAPI.socket.off('user-status-changed', callback);
    }
    return () => {}; // Return empty function if socket is null
  },

  // Task status events
  onTaskStatusUpdated: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('task-status-updated', callback);
      return () => chatAPI.socket && chatAPI.socket.off('task-status-updated', callback);
    }
    return () => {}; // Return empty function if socket is null
  },

  // Error handling
  onError: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('error', callback);
      return () => chatAPI.socket && chatAPI.socket.off('error', callback);
    }
    return () => {}; // Return empty function if socket is null
  },

  // Connection events
  onConnect: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('connect', callback);
      return () => chatAPI.socket && chatAPI.socket.off('connect', callback);
    }
    return () => {}; // Return empty function if socket is null
  },

  onDisconnect: (callback) => {
    if (chatAPI.socket) {
      chatAPI.socket.on('disconnect', callback);
      return () => chatAPI.socket && chatAPI.socket.off('disconnect', callback);
    }
    return () => {}; // Return empty function if socket is null
  }
};

// Calendar API functions
export const calendarAPI = {
  // Get all calendar events
  getEvents: (params = {}) => api.get('/calendar/events', { params }),
  
  // Get events for a specific date
  getEventsForDate: (date) => api.get(`/calendar/events/date/${date}`),
  
  // Get upcoming events
  getUpcomingEvents: (days = 7) => api.get('/calendar/events/upcoming', { params: { days } }),
  
  // Get single event
  getEvent: (id) => api.get(`/calendar/events/${id}`),
  
  // Create new event
  createEvent: (data) => api.post('/calendar/events', data),
  
  // Update event
  updateEvent: (id, data) => api.put(`/calendar/events/${id}`, data),
  
  // Delete event
  deleteEvent: (id) => api.delete(`/calendar/events/${id}`),
  
  // Get calendar statistics
  getStats: () => api.get('/calendar/stats'),
  
  // Bulk delete events
  bulkDeleteEvents: (eventIds) => api.delete('/calendar/events/bulk', { data: { eventIds } })
};

// Confidential Documents API
export const confidentialDocsAPI = {
  // Get confidential documents for a conversation
  getByConversation: (conversationId) => api.get(`/confidential-docs/conversation/${conversationId}`),
  
  // Upload confidential document
  uploadDocument: (formData) => api.post('/confidential-docs/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  
  // Update confidential document
  updateDocument: (id, data) => api.put(`/confidential-docs/${id}`, data),
  
  // Delete confidential document
  deleteDocument: (id) => api.delete(`/confidential-docs/${id}`),
  
  // Serve/download document
  serveDocument: (filename) => api.get(`/confidential-docs/serve/${filename}`, {
    responseType: 'blob'
  })
};

// Forms API functions
export const formsAPI = {
  // Get all forms (public and user's forms)
  getForms: (params = {}) => api.get('/forms', { params }),
  
  // Get forms created by the current user
  getMyForms: (params = {}) => api.get('/forms/my-forms', { params }),
  
  // Get single form by ID
  getForm: (id) => api.get(`/forms/${id}`),
  
  // Create new form
  createForm: (data) => api.post('/forms', data),
  
  // Update form
  updateForm: (id, data) => api.put(`/forms/${id}`, data),
  
  // Delete form
  deleteForm: (id) => api.delete(`/forms/${id}`),
  
  // Submit form
  submitForm: (id, data) => api.post(`/forms/${id}/submit`, data),
  
  // Get form submissions
  getFormSubmissions: (id, params = {}) => api.get(`/forms/${id}/submissions`, { params }),
  
  // Update form status
  updateFormStatus: (id, status) => api.put(`/forms/${id}/status`, { status }),
  
  // Get form analytics
  getFormAnalytics: (id) => api.get(`/forms/${id}/analytics`),
  
  // Duplicate form
  duplicateForm: (id) => api.post(`/forms/${id}/duplicate`),
  
  // Export form submissions
  exportSubmissions: (id, format = 'csv') => api.get(`/forms/${id}/export`, { 
    params: { format },
    responseType: 'blob'
  })
};

export default api; 